/**
 * Romanian public holidays configuration.
 *
 * Fixed holidays are generated algorithmically per year.
 * Movable holidays (Orthodox Easter-dependent) are listed explicitly
 * because Orthodox Easter requires a separate algorithm.
 */

// Fixed public holidays (DD-MM). Jan 6 & 7 became official from 2024.
const FIXED_HOLIDAYS = [
  { day: "01-01", label: "Anul Nou" },
  { day: "02-01", label: "Anul Nou (ziua 2)" },
  { day: "24-01", label: "Ziua Unirii Principatelor Romane" },
  { day: "01-05", label: "Ziua Muncii" },
  { day: "01-06", label: "Ziua Copilului", from: 2017 },
  { day: "15-08", label: "Adormirea Maicii Domnului" },
  { day: "30-11", label: "Sfantul Andrei" },
  { day: "01-12", label: "Ziua Nationala" },
  { day: "25-12", label: "Craciunul" },
  { day: "26-12", label: "Craciunul (ziua 2)" },
];

// Holidays added starting from a specific year
const FIXED_HOLIDAYS_WITH_START_YEAR = [
  { day: "06-01", label: "Boboteaza", from: 2024 },
  { day: "07-01", label: "Soborul Sf. Ioan Botezatorul", from: 2024 },
];

// Orthodox Easter Sunday dates (manually specified because
// the Orthodox computus is complex). Each entry gives the
// Easter-dependent movable holidays for that year:
//   Good Friday (Easter - 2), Easter Sunday, Easter Monday (Easter + 1),
//   Rusalii / Pentecost Sunday (Easter + 49), Pentecost Monday (Easter + 50)
const ORTHODOX_EASTER_DATES = {
  2021: new Date(2021, 4, 2), // May 2
  2022: new Date(2022, 3, 24), // Apr 24
  2023: new Date(2023, 3, 16), // Apr 16
  2024: new Date(2024, 4, 5), // May 5
  2025: new Date(2025, 3, 20), // Apr 20
  2026: new Date(2026, 3, 12), // Apr 12
  2027: new Date(2027, 4, 2), // May 2
  2028: new Date(2028, 3, 16), // Apr 16
  2029: new Date(2029, 3, 8), // Apr 8
  2030: new Date(2030, 3, 28), // Apr 28
};

function pad(n) {
  return String(n).padStart(2, "0");
}

function formatDate(date) {
  return `${pad(date.getDate())}-${pad(
    date.getMonth() + 1
  )}-${date.getFullYear()}`;
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function getMovableHolidays(easterSunday) {
  return [
    addDays(easterSunday, -2), // Good Friday
    easterSunday, // Easter Sunday
    addDays(easterSunday, 1), // Easter Monday
    addDays(easterSunday, 49), // Pentecost Sunday
    addDays(easterSunday, 50), // Pentecost Monday
  ];
}

/**
 * Returns an array of holiday strings in DD-MM-YYYY format
 * for the given year range (inclusive).
 */
export function getHolidays(startYear = 2021, endYear = 2030) {
  const holidays = [];

  const seen = new Set();

  for (let year = startYear; year <= endYear; year++) {
    // Fixed holidays
    for (const h of FIXED_HOLIDAYS) {
      if (h.from && year < h.from) continue;
      holidays.push(`${h.day}-${year}`);
    }

    // Fixed holidays with start year
    for (const h of FIXED_HOLIDAYS_WITH_START_YEAR) {
      if (year < h.from) continue;
      holidays.push(`${h.day}-${year}`);
    }

    // Movable holidays (Easter-dependent)
    const easter = ORTHODOX_EASTER_DATES[year];
    if (easter) {
      const movable = getMovableHolidays(easter);
      for (const date of movable) {
        holidays.push(formatDate(date));
      }
    }
  }

  // Remove duplicates (e.g. Jun 1 can be both fixed and movable)
  for (const h of holidays) {
    seen.add(h);
  }

  return [...seen];
}

export const HOLIDAY_FORMAT = "DD-MM-YYYY";
